// Global Instructions Rule Applied!
//
// Saves + restores the scroll position of the mobile shell's scroll container for a list
// view, keyed by the same listCache key as its rows. Pairs with useMobileList's cache: when
// you tap into a detail and come back, the list hydrates instantly AND lands where you left
// it. The scroll container is the shell's single <main> (overflowY:auto in MobileShell).

import { useEffect, useLayoutEffect, useRef } from 'react'
import { getCachedList, setCachedScrollTop } from './listCache.ts'

function scrollEl(): HTMLElement | null {
  return document.querySelector('main')
}

/**
 * @param cacheKey  the list's cache key (from useMobileList)
 * @param ready     true once rows are present, so restoring has content to scroll over
 */
export function useScrollRestore(cacheKey: string, ready: boolean): void {
  const restoredRef = useRef(false)

  // Continuously record scroll offset (rAF-throttled) and save once more on unmount.
  useEffect(() => {
    const el = scrollEl()
    if (!el) return
    let raf = 0
    const onScroll = () => {
      if (raf) return
      raf = window.requestAnimationFrame(() => {
        raf = 0
        setCachedScrollTop(cacheKey, el.scrollTop)
      })
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      el.removeEventListener('scroll', onScroll)
      if (raf) window.cancelAnimationFrame(raf)
      setCachedScrollTop(cacheKey, el.scrollTop)
    }
  }, [cacheKey])

  // Restore once per mount, after content is ready so the offset is reachable.
  useLayoutEffect(() => {
    if (restoredRef.current || !ready) return
    const cached = getCachedList(cacheKey)
    const el = scrollEl()
    if (el && cached && cached.scrollTop > 0) el.scrollTop = cached.scrollTop
    restoredRef.current = true
  }, [ready, cacheKey])
}
